import { useState } from 'react';
import logo from '@/assets/images/logo.png';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import * as z from 'zod';

import { confirmPasswordReset } from '@/hooks/auth';

import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form';
import { Input } from '../components/ui/input';

// Zod form to validate the password form
const formSchema = z
  .object({
    password: z
      .string({
        required_error: 'password cannot be empty',
      })
      .min(8, { message: 'password must be at least 8 characters' }),
    confirmPassword: z
      .string({
        required_error: 'confirm password cannot be empty',
      })
      .min(8, { message: 'confirm password must be at least 8 characters' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "passwords don't match",
    path: ['confirmPassword'],
  });

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [isTokenInvalid, setIsTokenInvalid] = useState(false);

  const token = searchParams.get('token');

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  async function onSubmit(values) {
    if (!token) {
      setIsTokenInvalid(true);
      return;
    }

    setIsLoading(true);
    setFormMessage('');
    setIsTokenInvalid(false);
    const { password } = values;

    await toast.promise(confirmPasswordReset(token, password), {
      loading: 'Resetting password...',
      success: (response) => {
        setIsSuccess(true);
        return response.message || 'Password has been successfully reset';
      },
      error: (error) => {
        const message =
          error.response?.data?.message || 'Failed to reset password';

        // Check if the error is specifically about invalid or expired token
        if (
          error.response?.status === 400 &&
          message === 'Invalid or expired reset token'
        ) {
          setIsTokenInvalid(true);
        } else {
          setFormMessage(message);
        }
        return message;
      },
    });

    setIsLoading(false);
  }

  if (!token || isTokenInvalid) {
    return (
      <div className='container relative flex h-screen items-center'>
        <div className='mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]'>
          <div className='flex h-16 shrink-0 items-center justify-center'>
            <img className='h-20 w-auto' src={logo} alt='Your Company' />
          </div>
          <div className='flex flex-col space-y-2 text-center'>
            <h1 className='text-2xl font-semibold tracking-tight'>
              Invalid Link
            </h1>
            <p className='text-sm text-muted-foreground'>
              This password reset link is invalid or has expired. Please request
              a new reset link or open the link from your email.
            </p>
          </div>
          <div className='text-center'>
            <Link
              to='/forgot-password'
              className='text-sm text-primary hover:underline'
            >
              Request a new reset link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className='container relative flex h-screen items-center'>
        <div className='mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]'>
          <div className='flex h-16 shrink-0 items-center justify-center'>
            <img className='h-20 w-auto' src={logo} alt='Your Company' />
          </div>
          <div className='flex flex-col space-y-2 text-center'>
            <h1 className='text-2xl font-semibold tracking-tight'>
              Password Reset
            </h1>
            <p className='text-sm text-muted-foreground'>
              Your password has been successfully reset. You can now log in with
              your new password.
            </p>
          </div>
          <div className='text-center'>
            <Link to='/login' className='text-sm text-primary hover:underline'>
              Go to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='container relative flex h-screen items-center'>
      <div className='mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]'>
        <div className='flex h-16 shrink-0 items-center justify-center'>
          <img className='h-20 w-auto' src={logo} alt='Your Company' />
        </div>
        <div className='flex flex-col space-y-2 text-center'>
          <h1 className='text-2xl font-semibold tracking-tight'>
            Reset Password
          </h1>
          <p className='text-sm text-muted-foreground'>
            Enter your new password below.
          </p>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className='grid gap-3'>
              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='New Password'
                        type='password'
                        autoCorrect='off'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='confirmPassword'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='Confirm Password'
                        type='password'
                        autoCorrect='off'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button disabled={isLoading} className='mt-2'>
                {isLoading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Reset Password
              </Button>
            </div>
            {formMessage && (
              <div className='my-3'>
                <Alert
                  variant='destructive'
                  className='cursor-default select-none py-2'
                >
                  <AlertDescription className='flex items-center justify-center'>
                    {formMessage}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </form>
        </Form>
        <div className='text-center'>
          <Link to='/login' className='text-sm text-primary hover:underline'>
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
